import { styled } from "@mui/material/styles";
import { ImageDimensions } from "csi/model/image";
import { useResizeObserver } from "designSystem/hooks";
import { debounce, throttle } from "lodash";
import { action, makeObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { ForwardRefRenderFunction, forwardRef, useEffect, useRef } from "react";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import TurntableIcon from "../icon/TurntableIcon";
import CsiImage from "../image/CsiImage";
import Box, { BoxProps } from "./Box";

const RootBox = styled(Box)({
	position: "relative",
	textAlign: "center",
	height: "100%",
	"& > .react-transform-wrapper > .react-transform-component": { height: "100%" },
});

const ImageBox = styled(Box)(({ theme }) => ({
	height: "100%",
	width: "100%",
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
}));

const AlignedTurntableIcon = styled(TurntableIcon)(({ theme }) => ({
	position: "absolute",
}));

const doubleClick = {
	/* disable the mouse double click zooming functionalities. */
	disabled: true,
};

const wheel = {
	/* sensitivity of zooming with the wheel/touchpad. */
	step: 0.8,
	/* disable the mouse wheel and touchpad zooming functionalities, it will NOT affect pinching. */
	disabled: true,
};

/** The min-width and min-height of the image container */
export interface MinWidthHeightImageContainerOptions {
	minHeight: number;
	minWidth: number;
}

/** Mouse Coordinates */
interface MouseCoordinates {
	x: number;
	y: number;
}

/** options for disabling panning */
export interface PanningOptions {
	disabled: boolean;
}

export interface ProgressiveImageData extends ImageDimensions {
	/** Preview image source */
	previewImageSrc: string;
}

export interface TurntableImageBoxProps extends BoxProps {
	/** aspect ratio of container that will affect calculation of height based on container width (width / aspectRatio) */
	aspectRatio?: number;

	/** to disable turntable feature if needed */
	disableTurntable?: boolean;

	/** conditionally hide the turntable icon (e.g. Lightbox) */
	hideIcon?: boolean;

	/** sequence of image sources to be shown (length 1: image, length > 1: turntable) */
	imageList: string[];

	/** image loaded flag */
	imageLoaded?: boolean;


	/** to set the min-width and min-height of the image container */
	minWidthHeightImageContainer?: MinWidthHeightImageContainerOptions;

	/** image loaded callback */
	onImageLoaded?: (e: React.SyntheticEvent<HTMLImageElement>) => void;

	/** callback for image container mouse scroll handling. */
	onMouseScroll?: (event: React.WheelEvent<HTMLDivElement>) => void;

	/** indicate whether image is padded or not. used for setting spacing on turntable icon */
	paddedImage?: boolean;

	/** panning options for the image */
	panning?: PanningOptions;

	/** preview image data for progressive loading */
	progressiveImageData?: ProgressiveImageData;
}

class TurntableStore {
	/** Aspect ratio */
	@observable aspectRatio: number;
	
	/** height of element containing image */
	@observable calculatedHeight: number = 0;

	/** width of element containing image */
	@observable calculatedWidth: number = 0;


	/** image caching flag */
	@observable imageCaching: boolean = false;

	/** index of the current image */
	@observable index: number = -1;


	/** mouse coordinates */
	@observable mouseCoordinates: MouseCoordinates = { x: 0, y: 0 };
	

	/** set image caching flag */
	@action setImageCaching = (imageCaching: boolean): void => {
		this.imageCaching = imageCaching;
	};

	/** set the index of the current image */
	@action setIndex = (index: number): void => {
		this.index = index;
	};

	/** set dimensions of the container element */
	@action setDimensions = (height: number, width: number): void => {
		if (!height || !width) {
			return;
		}
		const { aspectRatio } = this;
		this.calculatedHeight = aspectRatio ? (width / aspectRatio) : height;
		this.calculatedWidth = width;
	};

	/** set mouse coordinates */
	@action setMouseCoordinates = (mouseCoordinates: MouseCoordinates): void => {
		this.mouseCoordinates = mouseCoordinates;
	};
		
	/** set the aspect ratio */
	@action setAspectRatio = (aspectRatio: number): void => {
		this.aspectRatio = aspectRatio;
	};


	constructor( aspectRatio: number) {
		makeObservable(this);
		this.aspectRatio = aspectRatio;
	}
}

const TurntableImageBox: ForwardRefRenderFunction<ReactZoomPanPinchRef, TurntableImageBoxProps> = observer(forwardRef((props: TurntableImageBoxProps, transformWrapperRef : React.ForwardedRef<ReactZoomPanPinchRef>) => {
	const {
		aspectRatio,
		children,
		disableTurntable,
		imageList,
		imageLoaded,
		hideIcon,
		minWidthHeightImageContainer,
		onImageLoaded,
		onMouseScroll,
		paddedImage,
		panning,
		progressiveImageData,
		...otherProps
	} = props;
	const boxRef = useRef<HTMLInputElement>();
	const store = useLocalObservable<TurntableStore>(() => new TurntableStore(aspectRatio));

	const { 
		calculatedHeight,
		imageCaching,
		index,
		mouseCoordinates,
		setDimensions,
		setImageCaching,
		setIndex,
		setMouseCoordinates,
	} = store;
		
	useResizeObserver<HTMLDivElement>({
		// onResize callback is debounced because when you dynamically resize elements in the DOM, additional resize events are triggered, creating a loop.
		ref: boxRef,
		onResize: debounce(({ width, height }) => {
			setDimensions(height, width);
			
			// center the image in the container
			if (transformWrapperRef && typeof transformWrapperRef !== "function" && "current" in transformWrapperRef && transformWrapperRef.current) {
				transformWrapperRef.current.centerView();
			}
		}, 1),
	});
	
	const numImages = imageList.length;

	const onMouseMove = throttle((event: MouseEvent<HTMLDivElement>) => {
		const position = boxRef.current.getBoundingClientRect();
		const midpoint = Math.floor(position.width / 2);

		setMouseCoordinates({
			x: event.clientX - Math.floor(position.x),
			y: event.clientY - Math.floor(position.y),
		});

		const indexWidth = position.width / (numImages + 1);
		const widthsFromMidpoint = (mouseCoordinates.x - midpoint) / indexWidth;
		let nextIndex;
		if (widthsFromMidpoint >= 0) {
			nextIndex = Math.floor(widthsFromMidpoint);
		} else {
			nextIndex = Math.ceil(numImages + widthsFromMidpoint) % numImages;
		}

		// check that mouse crosses midpoint first, then any jump afterwards is okay
		if (nextIndex === 0 || index !== -1) {
			setIndex(nextIndex);
		}
	}, 25);

	function handleMouseMove(event) {
		onMouseMove(event);
	}

	function handleMouseLeave() {
		setIndex(-1);
	}
	useEffect(() => {
		if (!imageCaching && (index > 0) && (imageList.length > 1)){
			setImageCaching(true);
		}
	}, [index]);

	// determine height if using aspect ratio
	const resolvedHeight = (aspectRatio && calculatedHeight) ? calculatedHeight : "100%";

	return (
		<RootBox
			height={resolvedHeight}
			onMouseLeave={disableTurntable ? null : handleMouseLeave}
			onMouseMove={disableTurntable ? null : handleMouseMove}
			onWheel={onMouseScroll}	
			ref={boxRef}
			{...otherProps}
		>
			{/* TODO: Add progressive image loading for turntable images. Currently, previewImageSrc should be null if isTurntable is true */}
			{progressiveImageData && !imageLoaded &&
				<CsiImage scaleImageWithDimensions={{ height: progressiveImageData.height, width: progressiveImageData.width }} src={progressiveImageData.previewImageSrc} />
			}
			<TransformWrapper
				doubleClick={doubleClick}
				initialScale={1}
				panning={panning}
				ref={transformWrapperRef}
				wheel={wheel}
			>
				{({ zoomIn, zoomOut, resetTransform, setTransform, centerView, ...rest }) => (
					<TransformComponent wrapperStyle={{ height: "100%", width: "100%" }}>
						<ImageBox
							minHeight={minWidthHeightImageContainer?.minHeight}
							minWidth={minWidthHeightImageContainer?.minWidth}
						>
							{imageCaching && imageList.map((imgSrc, idx) => (
								<img
									key={idx}
									src={imgSrc}
									style={{ width:0, height:0, visibility: "hidden" }}
								/>
							))}
							<img
								onLoad={onImageLoaded}
								src={imageList[Math.max(0, index)]}
								style={{
									maxHeight: "100%",
									maxWidth: "100%",
									objectFit: "contain",
									pointerEvents: "auto",
								}}
							/>
						</ImageBox>
					</TransformComponent>
				)}
			</TransformWrapper>
			{
				imageList.length > 1 && !hideIcon && (
					<AlignedTurntableIcon
						sx={{
							right: paddedImage ? 4 : 2,
							bottom: paddedImage ? 4 : 2,
						}}
					/>
				)
			}
		</RootBox>
	);
}));

export default TurntableImageBox;
